/**
 * HomeLanding — public splash at hardinbachat.com/
 * ----------------------------------------------------------------
 * Acts as the front-facing "company website" so customers landing on the
 * root domain don't see the billing app login.  Three clear paths:
 *   1. Order groceries   → /shop (existing clients)
 *   2. Sign up           → /register (new clients)
 *   3. Admin / Billing   → /login (staff)
 * Also functions as the domain's SEO-friendly front page.
 */
import { Link } from "react-router-dom";
import { ShoppingBag, UserPlus, ShieldCheck, Mail, MapPin, Sparkles, Mic, PackageCheck, Store, ListChecks, Home, Building2, ArrowRight } from "lucide-react";

const HDB_LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_pdf-guide-dev/artifacts/7s3f15d0_HDB%20LOGO.webp";

export default function HomeLanding() {
    return (
        <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50/40" data-testid="home-landing">
            {/* ── Header ── */}
            <header className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-white shadow-md p-1.5 grid place-items-center">
                        <img src={HDB_LOGO} alt="HDB" className="w-full h-full object-contain"/>
                    </div>
                    <div>
                        <div className="text-lg font-black tracking-tight text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Har Din Bachat
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">HDB Retail</div>
                    </div>
                </div>
                <Link to="/login" className="text-xs font-bold text-slate-600 hover:text-slate-900 hidden sm:inline-flex items-center gap-1"
                      data-testid="home-admin-link">
                    <ShieldCheck size={12}/> Admin
                </Link>
            </header>

            {/* ── Hero ── */}
            <section className="max-w-6xl mx-auto px-5 pt-6 pb-10 md:pt-16 md:pb-20">
                <div className="grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-widest">
                            <Sparkles size={11}/> Delivering in Dehradun &amp; Bangalore
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black text-slate-900 mt-4 leading-[1.05]"
                            style={{fontFamily:"'Chivo',sans-serif"}}>
                            Any &amp; every monthly essential,
                            <span className="text-emerald-700 block">delivered to your doorstep.</span>
                        </h1>
                        <p className="text-slate-700 text-lg italic mt-4 font-medium">
                            “What we deliver is savings.<br className="hidden sm:block"/>Rest, all is just delivery.”
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/shop"
                                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white font-black shadow-lg hover:shadow-xl active:scale-[0.98] transition-all"
                                  data-testid="home-order-cta">
                                <ShoppingBag size={16}/> Order groceries
                            </Link>
                            <Link to="/register"
                                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white border-2 border-emerald-600 text-emerald-700 font-black hover:bg-emerald-50 active:scale-[0.98] transition-all"
                                  data-testid="home-register-cta">
                                <UserPlus size={16}/> New here? Sign up
                            </Link>
                        </div>

                        <p className="text-xs text-slate-500 mt-4">
                            Households + Businesses · Instant portal · No account fee · GSTIN optional
                        </p>
                    </div>

                    {/* Right visual — big logo */}
                    <div className="relative hidden md:block">
                        <div className="absolute -inset-8 bg-gradient-to-tr from-emerald-100 via-yellow-50 to-white rounded-full blur-3xl"/>
                        <div className="relative bg-white rounded-3xl shadow-2xl p-10 border-4 border-emerald-100">
                            <img src={HDB_LOGO} alt="HDB" className="w-full h-64 object-contain"/>
                            <div className="mt-6 text-center">
                                <div className="text-xl font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                                    Har Din Bachat
                                </div>
                                <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mt-1">
                                    Save every day
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Essentials planner ── */}
            <section className="max-w-6xl mx-auto px-5 pb-10" data-testid="home-planner-section">
                <div className="rounded-3xl bg-white border-2 border-emerald-100 p-6 md:p-8 grid md:grid-cols-[1.2fr_1fr] gap-6 items-center">
                    <div>
                        <div className="inline-flex items-center gap-1.5 text-emerald-700 text-[10px] font-black uppercase tracking-widest"><ListChecks size={12}/> New</div>
                        <h2 className="text-2xl md:text-3xl font-black text-slate-900 mt-1" style={{fontFamily:"'Chivo',sans-serif"}}>Plan your essentials</h2>
                        <p className="text-slate-600 text-sm mt-2">Build your monthly list once — for your home or your hotel, restaurant, school or office. Save it, reuse it, and get one quotation for the whole basket.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Link to="/portal/essentials-planner/home/start" data-testid="home-planner-home"
                              className="rounded-2xl bg-emerald-600 text-white p-4 font-black hover:bg-emerald-500 transition-colors">
                            <Home size={20}/><div className="mt-2">For my home</div><ArrowRight size={14} className="mt-1"/>
                        </Link>
                        <Link to="/portal/essentials-planner/business/start" data-testid="home-planner-business"
                              className="rounded-2xl bg-slate-900 text-white p-4 font-black hover:bg-slate-800 transition-colors">
                            <Building2 size={20}/><div className="mt-2">For my business</div><ArrowRight size={14} className="mt-1"/>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Feature strip ── */}
            <section className="max-w-6xl mx-auto px-5 pb-10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <FeatureCard icon={<Store size={22}/>} title="Retail store" body="Since day one, trusted household staple." />
                    <FeatureCard icon={<PackageCheck size={22}/>} title="Home delivery" body="Right to your address, on schedule." />
                    <FeatureCard icon={<Mic size={22}/>} title="Voice order" body="Speak your list in Hindi, Gujarati, or English." />
                    <FeatureCard icon={<ShieldCheck size={22}/>} title="B2B & B2C" body="Businesses get GST invoices instantly." />
                </div>
            </section>

            {/* ── Contact strip ── */}
            <section className="max-w-6xl mx-auto px-5 pb-16">
                <div className="rounded-3xl bg-slate-900 text-white p-8 md:p-10 grid md:grid-cols-2 gap-6 items-center">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-black" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Ready to save on your monthly essentials?
                        </h2>
                        <p className="text-slate-300 mt-2 text-sm">
                            Sign up in a minute · Start ordering right away · Cancel anytime.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3 md:justify-end">
                        <Link to="/register"
                              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 text-white font-black hover:bg-emerald-400 active:scale-[0.98]"
                              data-testid="home-cta-register-2">
                            <UserPlus size={14}/> Sign up
                        </Link>
                        <Link to="/shop"
                              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-slate-900 font-black hover:bg-slate-100 active:scale-[0.98]"
                              data-testid="home-cta-order-2">
                            <ShoppingBag size={14}/> I'm already a customer
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="max-w-6xl mx-auto px-5 py-6 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-3 border-t border-slate-200">
                <div className="flex items-center gap-2">
                    <img src={HDB_LOGO} alt="" className="w-6 h-6"/>
                    <span>© Har Din Bachat · HDB Retail</span>
                </div>
                <div className="flex items-center gap-4">
                    <a href="mailto:hello@hardinbachat.com" className="hover:text-slate-900 inline-flex items-center gap-1" data-testid="home-email">
                        <Mail size={11}/> hello@hardinbachat.com
                    </a>
                    <span className="inline-flex items-center gap-1" data-testid="home-cities"><MapPin size={11}/> Delivering in Dehradun &amp; Bangalore</span>
                </div>
            </footer>
        </div>
    );
}

function FeatureCard({ icon, title, body }) {
    return (
        <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center mb-2">
                {icon}
            </div>
            <div className="font-black text-slate-900 text-sm">{title}</div>
            <div className="text-xs text-slate-500 mt-1 leading-snug">{body}</div>
        </div>
    );
}
